import React from "react";
import {
  WhmStack,
  WhmCheckbox,
  WhmTypography,
  WhmAvatar,
  WhmBox,
} from "@securitize/reactjs-whm";
import { AssetWithPosition } from "../../../data/types/portfolio-statement/portfolio-statement";

const NETWORK_SHORT_NAMES: Record<string, string> = {
  Algorand: "Algo",
  Base: "Base",
  Converge: "Cvg",
  "Ethereum - Sepolia": "Ethsep",
  Hedera: "Hbar",
  HyperEVM: "Hype",
  "Injective EVM": "Inevm",
  Mantle: "Mnt",
  PLUME: "Plume",
  "Tempo Testnet": "Temp",
  "Tron - Nile": "Trx",
  "XRPL Testnet": "Xrpl",
};

const shortNetwork = (name: string): string =>
  // The map provides short labels; if the network is not listed,
  // use an uppercase first character and the next two characters in lowercase.
  NETWORK_SHORT_NAMES[name] ??
  name.charAt(0).toUpperCase() + name.slice(1, 3).toLowerCase();

interface AssetSelectionItemProps {
  asset: AssetWithPosition;
  isSelected: boolean;
  onToggle: (assetId: string) => void;
}

export const AssetSelectionItem: React.FC<AssetSelectionItemProps> = React.memo(
  ({ asset, isSelected, onToggle }) => {
    const handleChange = () => {
      onToggle(asset.id);
    };

    return (
      <WhmStack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          py: 1.5,
          "&:hover": { backgroundColor: "action.hover" },
          cursor: "pointer",
        }}
        onClick={handleChange}
      >
        {/* Token logo with issuer overlay */}
        <WhmBox sx={{ position: "relative", flexShrink: 0 }}>
          <WhmAvatar
            src={asset.tokenLogoUrl}
            alt={asset.tokenName}
            sx={{
              width: 36,
              height: 36,
              bgcolor: "grey.200",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {asset.tokenSymbol.charAt(0)}
          </WhmAvatar>
          {asset.issuerLogoUrl && (
            <WhmAvatar
              src={asset.issuerLogoUrl}
              alt={asset.issuerName}
              sx={{
                width: 18,
                height: 18,
                position: "absolute",
                bottom: -2,
                right: -2,
                border: "2px solid",
                borderColor: "background.paper",
                bgcolor: "grey.300",
                fontSize: 8,
              }}
            >
              {asset.issuerName.charAt(0)}
            </WhmAvatar>
          )}
        </WhmBox>

        {/* Token full name */}
        <WhmBox sx={{ flex: 1, minWidth: 0 }}>
          <WhmTypography variant="body2" noWrap>
            {asset.tokenName}
            {asset.networkName && ` (${shortNetwork(asset.networkName)})`}
          </WhmTypography>
        </WhmBox>

        {/* Checkbox on the right */}
        <WhmCheckbox
          checked={isSelected}
          onChange={handleChange}
          onClick={(e) => e.stopPropagation()}
          inputProps={{
            "aria-label": `Select ${asset.tokenName}${asset.networkName ? ` (${shortNetwork(asset.networkName)})` : ""}`,
          }}
          size="small"
        />
      </WhmStack>
    );
  },
);

AssetSelectionItem.displayName = "AssetSelectionItem";
